import {
  Identifier,
  Record as RaRecord,
  SortPayload,
  useDataProvider,
  useListFilterContext,
} from 'react-admin';
import { useState, useEffect, useMemo } from 'react';
import { Autocomplete } from '@material-ui/lab';

import { useLazyReferenceOptions } from 'hooks';
import {
  LAZY_AUTOCOMPLETE_STYLE,
  LazyReferenceTextField,
  OptionText,
  optionLabelOf,
  sameOptionBy,
} from 'components/LazyReferenceAutocomplete';

interface LazyReferenceArrayInputProps {
  label: string;
  reference: string;
  source: string;
  filterToQuery?: (search: string) => Record<string, unknown> | undefined;
  sort?: SortPayload;
  perPage?: number;
  optionText?: OptionText;
  optionValue?: string;
  debounceTime?: number;
  // Read by react-admin's <Filter>, not by this input.
  alwaysOn?: boolean;
  'data-cy'?: string;
}

const NO_IDS: Identifier[] = [];

export const LazyReferenceArrayInput = ({
  label,
  reference,
  source,
  filterToQuery = search => (search ? { name: search } : {}),
  sort = { field: 'name', order: 'ASC' },
  perPage = 25,
  optionText = 'name',
  optionValue = 'id',
  debounceTime = 500,
  'data-cy': dataCy,
}: LazyReferenceArrayInputProps) => {
  const dataProvider = useDataProvider();
  const { setFilters, filterValues, displayedFilters } = useListFilterContext();

  const { open, setOpen, options, loading, setInputValue } =
    useLazyReferenceOptions({
      label,
      debounceTime,
      loadOptions: search =>
        dataProvider
          .getList(reference, {
            pagination: { page: 1, perPage },
            sort,
            filter: filterToQuery(search),
          })
          .then(({ data }) => data),
    });

  // The filter owns the selection; this only remembers the record behind each
  // id so it can be shown as a tag. null marks an id the API no longer
  // returns, so it is not requested again.
  const [byId, setById] = useState<Record<Identifier, RaRecord | null>>({});

  const ids: Identifier[] = filterValues[source] ?? NO_IDS;

  useEffect(() => {
    const missing = ids.filter(id => !(id in byId));
    if (!missing.length) {
      return;
    }

    let cancelled = false;

    dataProvider
      .getMany(reference, { ids: missing })
      .then(({ data }) => {
        if (!cancelled) {
          setById(prev => ({
            ...prev,
            ...Object.fromEntries(
              missing.map(id => [
                id,
                data.find(item => item[optionValue] === id) ?? null,
              ]),
            ),
          }));
        }
      })
      .catch(err => {
        if (!cancelled) {
          console.error(
            '[LazyReferenceArrayInput] Error loading selected options:',
            err,
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [ids, byId, reference, dataProvider, optionValue]);

  // Autocomplete resets the typed text whenever `value` changes identity, so
  // it must only change when the selection does.
  const value = useMemo(
    () =>
      ids.map(id => byId[id]).filter((item): item is RaRecord => Boolean(item)),
    [ids, byId],
  );

  return (
    <Autocomplete
      data-cy={dataCy}
      multiple
      limitTags={1}
      ChipProps={{ size: 'small' }}
      style={LAZY_AUTOCOMPLETE_STYLE}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      getOptionLabel={optionLabelOf(optionText)}
      getOptionSelected={sameOptionBy(optionValue)}
      loading={loading}
      // MUI empties the text after every pick and on blur; searching with what
      // the box shows keeps the next open from listing a stale search.
      onInputChange={(_event, newInputValue) => setInputValue(newInputValue)}
      value={value}
      onChange={(_, newValue) => {
        setById(prev => ({
          ...prev,
          ...Object.fromEntries(
            newValue.map(option => [option[optionValue], option]),
          ),
        }));

        const newFilters = { ...filterValues };
        if (newValue.length) {
          newFilters[source] = newValue.map(option => option[optionValue]);
        } else {
          delete newFilters[source];
        }

        setFilters(newFilters, displayedFilters, false);
      }}
      renderInput={params => (
        <LazyReferenceTextField
          params={params}
          label={label}
          loading={loading}
        />
      )}
    />
  );
};
